import { env } from '@/lib/runtime-env'

const ensureEnvVariables = <T = unknown>(value: T, fn: string) => {
  // use == to check if value is null or undefined
  if (value == null) {
    throw new Error(
      `Environment variable is not set, please check your ${fn} function or .env file`,
    )
  }

  return value
}

export const getWorldsDomain = () => {
  return ensureEnvVariables(env('NEXT_PUBLIC_WORLDS_DOMAIN'), 'getWorldsDomain')
}

export const getWorldsBaseUrl = (locale?: string) => {
  const lp = locale && locale !== 'en-US' ? `/${locale}` : ''
  return `https://${getWorldsDomain()}${lp}`
}

export const getViversePlusSgeRedeemCode = () => {
  return ensureEnvVariables(
    env('NEXT_PUBLIC_VIVERSE_PLUS_SGE_REDEEM_CODE'),
    'getViversePlusSgeRedeemCode',
  )
}

export const getCreateDomain = () => {
  return ensureEnvVariables(env('NEXT_PUBLIC_CREATE_DOMAIN'), 'getCreateDomain')
}

/**
 * Parent domain the HTC SSO cookies are written on.
 *
 * Must mirror Reticulum's `htc_account_sso_cookie_domain`: clearing a cookie only matches on
 * name + domain + path, so a drift here silently turns cookie removal into a no-op.
 */
export const getHtcAuthCookieDomain = () => {
  return ensureEnvVariables(env('NEXT_PUBLIC_HTC_AUTH_COOKIE_DOMAIN'), 'getHtcAuthCookieDomain')
}

export const getCurrentDomain = () => {
  const hostname = window.location.hostname

  const createDomain = getCreateDomain()
  const worldsDomain = getWorldsDomain()

  return createDomain?.includes(hostname) ? createDomain : worldsDomain || hostname
}

export const getCmsBaseURL = (isSSR: boolean = false) => {
  if (isSSR) {
    return ensureEnvVariables(env('CMS_SERVER'), 'getCmsBaseURL')
  }
  return ensureEnvVariables(env('NEXT_PUBLIC_CMS_SERVER'), 'getCmsBaseURL')
}

export const getShareComponentSDKUri = () => {
  const shareComponentSDKUri =
    env('NEXT_PUBLIC_SHARE_COMPONENT_SDK_URI') || process.env.NEXT_PUBLIC_SHARE_COMPONENT_SDK_URI

  return ensureEnvVariables(shareComponentSDKUri, 'getShareComponentSDKUri')
}

export const getViveAvatarDomain = () => {
  return ensureEnvVariables(env('NEXT_PUBLIC_VIVE_AVATAR_DOMAIN'), 'getViveAvatarDomain')
}

export const getCommunityServiceBaseURL = () => {
  return ensureEnvVariables(
    env('NEXT_PUBLIC_COMMUNITY_SERVICE_BASE_URL'),
    'getCommunityServiceBaseURL',
  )
}

export const getMarketBaseURL = () => {
  const isLocal = process.env.NODE_ENV === 'development'

  if (isLocal) {
    return '/api/market/proxy'
  }
  return ensureEnvVariables(env('NEXT_PUBLIC_MARKET_SERVER'), 'getMarketBaseURL')
}

export const getMarketBrowserBaseURL = () => {
  return ensureEnvVariables(env('NEXT_PUBLIC_MARKET_SERVER'), 'getMarketBrowserBaseURL')
}

export const getViversePlusSgeRedeemUrl = () => {
  const redeemUrl = new URL('/redeem', getMarketBrowserBaseURL())
  redeemUrl.searchParams.set('code', getViversePlusSgeRedeemCode())
  return redeemUrl.href
}

export const enableExamplePage = () => {
  return process.env.NODE_ENV === 'development'
}

export const enableLoginStatusCheck = () => {
  return process.env.NODE_ENV !== 'development'
}

export const enableDevShareComponent = () => {
  return process.env.NODE_ENV === 'development' && typeof window !== 'undefined'
}

export const enableCookieDomain = () => {
  return process.env.NODE_ENV !== 'development'
}

export const enableGoogleAd = () => {
  return env('NEXT_PUBLIC_ENABLE_GOOGLE_AD') === 'true'
}

export const enableFcm = () => {
  return env('NEXT_PUBLIC_ENABLE_FCM') === 'true'
}

export const enableWatchHistory = () => {
  return env('NEXT_PUBLIC_ENABLE_WATCH_HISTORY') === 'true'
}

export const getHiddenWorldTags = () => {
  return (env('NEXT_PUBLIC_HIDDEN_WORLD_TAGS') || '').split('|').filter(Boolean)
}

export const isAdFreeWorld = (tags: (string | { name: string })[]) => {
  const adFreeTags = (env('NEXT_PUBLIC_AD_FREE_WORLD_TAGS') || '').split('|').filter(Boolean)
  return tags.some((tag) => adFreeTags.includes(typeof tag === 'string' ? tag : tag.name))
}

export const getDisablePosthogRecordingRoomIds = (): string[] => {
  return (env('NEXT_PUBLIC_DISABLE_POSTHOG_RECORDING') || '').split('|').filter(Boolean)
}
export const enableMobileBrowserGating = () => {
  return env('NEXT_PUBLIC_ENABLE_MOBILE_BROWSER_GATING') === 'true'
}

// Invalid or missing values fall back to the product default so quality
// filtering never silently turns off from a bad env value. A blank env must
// fall back, not resolve to 0 (Number('') === 0, which the backend reads as
// "no filter"); an explicit "0" is a valid off-switch.
const getGameScorePoints = (key: string, fallback: number) => {
  const raw = env(key)?.trim()
  if (!raw) return fallback
  const value = Number(raw)
  return Number.isInteger(value) && value >= 0 && value <= 100 ? value : fallback
}

export const getGamesPageGameScore = () =>
  getGameScorePoints('NEXT_PUBLIC_GAME_SCORE_GAMES_PAGE', 70)

export const getLandingGameScore = () => getGameScorePoints('NEXT_PUBLIC_GAME_SCORE_LANDING', 80)

export const getNewPageGameScore = () => getGameScorePoints('NEXT_PUBLIC_GAME_SCORE_NEW_PAGE', 80)
